import { RoundedComment, Shop, Specifications, Versus } from '@zoomit/icons';

import azkivamLogo from '../assets/images/installment-provider/azkivam-logo.png';
import snappPayLogo from '../assets/images/installment-provider/snapp-pay.png';
import technopayLogo from '../assets/images/installment-provider/technopay.png';
import wepodLogo from '../assets/images/installment-provider/wepod.png';
import type { ChartRange } from '../types/single-product';

export const SPECIFICATIONS_ID = 'spec';
export const STORES_ID = 'price';
export const PHYSICAL_STORES_ID = 'physical-stores';
export const PRICE_HISTORY_ID = 'price-history';
export const RELATED_PRODUCT_ID = 'related-product';
export const COMPARE_ID = 'compare';

export const OVERVIEW_ID = 'overview';
export const SCORE_ID = 'user-score';
export const COMMENT_ID = 'comment';

type NavigationItemsArgs = {
  hasPriceChart: boolean;
  hasOverview: boolean;
  hasCompare: boolean;
  feedbackCount?: number;
};

export const navigationItems = ({
  hasPriceChart,
  hasOverview,
  hasCompare,
  feedbackCount,
}: NavigationItemsArgs) => [
  {
    label: 'مشخصات',
    id: SPECIFICATIONS_ID,
    show: true,
  },
  {
    label: 'قیمت و فروشندگان',
    id: STORES_ID,
    show: true,
  },
  {
    label: 'تاریخچه قیمت',
    id: PRICE_HISTORY_ID,
    show: hasPriceChart,
  },
  {
    label: 'مقایسه با رقبا',
    id: COMPARE_ID,
    show: hasCompare,
  },
  {
    label: 'محصولات مشابه',
    id: RELATED_PRODUCT_ID,
    show: true,
  },
  {
    label: 'جمع بندی',
    id: OVERVIEW_ID,
    show: hasOverview,
  },
  {
    label: 'دیدگاه و سوال',
    id: COMMENT_ID,
    show: true,
    count: feedbackCount,
  },
];

export const mobileNavigationItems = ({ hasCompare }: { hasCompare: boolean }) => {
  const navItems = [
    {
      label: 'مشخصات',
      id: SPECIFICATIONS_ID,
    },
    {
      label: 'فروشندگان',
      id: STORES_ID,
    },
  ];
  if (hasCompare) {
    navItems.push({
      label: 'مقایسه',
      id: COMPARE_ID,
    });
  }

  navItems.push({
    label: 'نظرات',
    id: COMMENT_ID,
  });

  return navItems;
};

export const sellersSort = [
  { value: 'Cheapest', label: 'ارزان‌ترین' },
  { value: 'MostExpensive', label: 'گران ترین' },
  { value: 'StoreName', label: 'ترتیب الفبا' },
];

export const bottomStickyMenuItems = [
  { label: 'مشخصات', icon: Specifications, id: SPECIFICATIONS_ID },
  { label: 'فروشندگان', icon: Shop, id: STORES_ID },
  { label: 'مقایسه', icon: Versus, id: COMPARE_ID },
  { label: 'نظرات', icon: RoundedComment, id: COMMENT_ID },
];

export const priceHistoryRanges: { value: ChartRange; label: string; agoMonth: number }[] = [
  { value: 'All', label: 'نمایش از ابتدا', agoMonth: 0 },
  { value: 'LastMonth', label: 'نمایش یک‌ ماهه', agoMonth: 1 },
  { value: 'LastThreeMonths', label: 'نمایش سه ماهه', agoMonth: 3 },
  { value: 'LastSixMonths', label: 'نمایش شش ماهه', agoMonth: 6 },
  { value: 'LastYear', label: 'نمایش یک‌ ساله', agoMonth: 12 },
];

export const relatedProductsChips = (productBrand: string) => [
  { id: 'SimilarPrice', title: 'محصولات با قیمت مشابه' },
  { id: 'SimilarSpecification', title: 'محصولات با مشخصات مشابه' },
  { id: 'SimilarBrand', title: `محصولات برند ${productBrand}` },
  { id: 'MostCompared', title: 'محصولات بیشتر مقایسه شده' },
];

export const availabilityZoneLabels = {
  ComingSoon: 'به زودی',
  Rumor: 'در حد شایعه',
  Supplied: '',
  Canceled: 'لغو شده',
};

type InstallmentProvider = {
  id: string;
  title: string;
  description: string;
  url: string;
  logo: string;
  buttonLabel: string;
  tagClassName: string;
};

export const availableInstallmentProviders: InstallmentProvider[] = [
  {
    id: 'wepod',
    title: 'ویپاد',
    description: 'تا ۳۰۰ میلیون تومان، بدون ضامن و آنلاین تسهیلات بگیرید',
    url: 'https://trc.metrix.ir/dcvw2x/',
    logo: wepodLogo,
    buttonLabel: 'دریافت تسهیلات ویپاد',
    tagClassName: 'border-[#2BB675]',
  },
  {
    id: 'snapp',
    title: 'اسنپ‌پی',
    description: 'تا 100 میلیون تومان | بدون ضامن، اقساط تا 24 ماه',
    url: 'https://l.snpy.ir/8fiux',
    logo: snappPayLogo,
    buttonLabel: 'دریافت اعتبار',
    tagClassName: 'border-[#008EFA]',
  },
  {
    id: 'azkivam',
    title: 'از‌کی وام',
    description: 'درخواست وام تا ۱۰۰ میلیون، ۱۸ قسط با سود سالانه ۲۲ درصد 💶',
    url: 'https://azkivam.com/?utm_source=zoomit&utm_medium=pdp_box&utm_campaign=1-mahe',
    logo: azkivamLogo,
    buttonLabel: 'درخواست وام ازکی‌ وام',
    tagClassName: 'border-[#095BED]',
  },
  {
    id: 'technopay',
    title: 'تکنوپی',
    description: 'تا ۱۵۰ میلیون تومان | آنلاین بدون نیاز به سپرده و ضامن! | اقساط ۱۸ ماهه',
    url: 'https://technopay.ir/?utm_source=zoomit&utm_medium=box&utm_campaign=general&utm_content=01',
    logo: technopayLogo,
    buttonLabel: 'دریافت تسهیلات تکنوپی',
    tagClassName: 'border-[#00B7CE]',
  },
];
